import {
  html,
  customElement,
  property,
  state,
} from '@oskecare/fe-utilities/lib/LitElement/index.js';
import { bound } from '@oskecare/fe-utilities/lib/decorators';
import OrangeL2Widget from '@oskecare/fe-utilities/lib/OrangeL2Widget';
import { STAT_CATEGORY_OTHER, STAT_GROUP_SSO } from '@oskecare/fe-utilities/lib/constants/stats';
import { delay } from '@oskecare/fe-utilities/scripts/helpers';
import {
  authManager,
  customerDataManager,
  messageBus,
  router,
  tagManager,
} from '../infrastructure.js';
import { DELAY_BETWEEN_SINGLE_SN_CALLS, MAX_SN_COUNT_FOR_SINGLE_SN_CALLS } from './constants.js';

/**
 * Wrapper (L2) element for MegaMenu widget
 */
@customElement('osk-mega-menu-wrapper')
class MegaMenu extends OrangeL2Widget {
  /**
   * @type {string}
   */
  logSource = 'mega-menu-wrapper';

  /**
   * @type {string}
   */
  @property({ attribute: 'active-item', type: String })
  accessor activeItem = '';

  /**
   * @type {string}
   */
  @state()
  accessor _loggedInSn = '';

  /**
   * @type {?boolean}
   */
  @state()
  accessor _isPrepaid = null;

  /**
   * @type {boolean}
   */
  @state()
  accessor _hasGigamarketAccess = false;

  /**
   * @type {object[]}
   */
  @state()
  accessor _identityList = [];

  /**
   * @type {string}
   */
  @state()
  accessor _legalType = 'PRIVA';

  /**
   * @type {number}
   */
  @state()
  accessor _unseenClaimsAndRequestsMessages = 0;

  firstUpdated() {
    super.firstUpdated();
    this.getData();
  }

  async getData() {
    await super.getData({
      fetch: async () => {
        await this.fetchIdentityData();
        await this.fetchGigamarketAccess();
        await this.fetchLegalType();
        await this.fetchUnseenClaimsAndRequestsMessages();
      },
    });
  }

  /**
   * Fetch identity list and required user data
   *
   * @returns {Promise<void>}
   */
  async fetchIdentityData() {
    this._identityList = await customerDataManager.getIdentityList();

    const customerData = await this.infrastructure.customerDataManager.getCustomerData();
    this._loggedInSn = customerData.loggedInSnData.sn;
    this._isPrepaid = customerData.cnData.isPrepaid;
  }

  /**
   * Fetch product name to determine whether the customer as gigamarket access (only Yoxo users).
   *
   * @returns {Promise<void>}
   */
  async fetchGigamarketAccess() {
    const customerProductData = (
      await this.infrastructure.products.inventory.getBasicProduct(this._loggedInSn)
    ).singleItem;

    this._hasGigamarketAccess = ['Go Safe Yoxo', 'Yoxo paušál'].includes(customerProductData.name); // TODO: swap for ID and move it to config
  }

  /**
   * Fetch customer's legal type.
   *
   * @returns {Promise<void>}
   */
  async fetchLegalType() {
    const { customerId } = (await this.infrastructure.customerDataManager.getCustomerData()).cnData;

    if (!customerId) {
      return;
    }

    const customerAccountInfo = await this.infrastructure.customerDataManager
      .getCustomerAccountInfo(true)
      .catch(() => null);

    this._legalType = customerAccountInfo?.legalType ?? 'PRIVA';
  }

  /**
   * Sets the number of claims with unseen messages of all SNs under the current CN.
   *
   * All the updates for each individual claim are represented as one updated unseen claim.
   * We sum these unseen claims for each SN and after that we sum those as well.
   *
   * Note: Bound because of 'claims.seen' subscription, see `connectedCallback`
   *
   * @returns {Promise<void>}
   */
  @bound
  async fetchUnseenClaimsAndRequestsMessages() {
    const customerData = await this.infrastructure.customerDataManager
      .getCustomerData()
      .catch(() => null);
    if (customerData === null) {
      this._unseenClaimsAndRequestsMessages = 0;
      return;
    }
    const numberOfSns = customerData.snDataList.length;
    if (numberOfSns > MAX_SN_COUNT_FOR_SINGLE_SN_CALLS) {
      this._unseenClaimsAndRequestsMessages = 0;
      return;
    }

    const promises = customerData.snDataList.map(async (snData, index) => {
      await delay(index * DELAY_BETWEEN_SINGLE_SN_CALLS);
      return this.infrastructure.claims
        .getClaimsList(snData.subscriptionId, true)
        .catch(() => null)
        .then(data => {
          if (!data) {
            return 0;
          }
          return data.reduce(
            (sum, claim) =>
              claim.statusChange.some(change => !change.seenByCustomer) ? sum + 1 : sum,
            0,
          );
        });
    });
    const listOfUnseenClaims = await Promise.all(promises);
    this._unseenClaimsAndRequestsMessages = listOfUnseenClaims.reduce(
      (accumulator, number) => accumulator + number,
      0,
    );
  }

  /**
   * Navigation event handler
   * @param {CustomEvent} event - Custom event with detail: { path: String, absolute: String }
   */
  static navigateHandler(event) {
    const { path, absolute } = event.detail;
    router.navigate(path, absolute);
  }

  /**
   * Logout event handler
   */
  static logoutHandler() {
    authManager.logout();
  }

  /**
   * Switch identity event handler
   * @param {CustomEvent} event - Custom event with detail: { id: String }
   */
  switchIdentityHandler(event) {
    this.logStat('Identity Switch', STAT_GROUP_SSO, STAT_CATEGORY_OTHER);
    authManager.logoutPartially(event.detail.id);
  }

  /**
   * Push mHub events from MegaMenu
   *
   * @param event
   */
  static mhubPush(event) {
    tagManager.mhubPush(event.detail);
  }

  /**
   * Lifecycle callback in the custom elements API
   * Fires each time a custom element is appended into a document-connected element
   */
  connectedCallback() {
    if (super.connectedCallback) {
      super.connectedCallback();
    }
    messageBus.subscribe('notifications', 'claims.seen', this.fetchUnseenClaimsAndRequestsMessages);
  }

  /**
   * Lifecycle callback in the custom elements API
   * Fires each time a custom element is removed from a document-connected element
   */
  disconnectedCallback() {
    messageBus.unsubscribe(
      'notifications',
      'claims.seen',
      this.fetchUnseenClaimsAndRequestsMessages,
    );
    if (super.disconnectedCallback) {
      super.disconnectedCallback();
    }
  }

  render() {
    return html`
      <osk-mega-menu
        ?is-prepaid=${this._isPrepaid}
        ?has-gigamarket-access=${this._hasGigamarketAccess}
        logged-in-identity=${this._loggedInSn}
        available-identities=${JSON.stringify(this._identityList)}
        active-item=${this.activeItem}
        legal-type=${this._legalType}
        ?is-single-sn=${this.infrastructure.customerDataManager.isSingleSn}
        unseen-claims-and-requests-msgs=${this._unseenClaimsAndRequestsMessages}
        @navigate=${MegaMenu.navigateHandler}
        @switch-identity=${this.switchIdentityHandler}
        @logout=${MegaMenu.logoutHandler}
        @mhub-push=${MegaMenu.mhubPush}
      ></osk-mega-menu>
    `;
  }
}

export default MegaMenu;
